import { BedrockRuntimeClient, ConverseCommand, ConverseStreamCommand } from '@aws-sdk/client-bedrock-runtime';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { fetchAuthSession } from 'aws-amplify/auth';
import { awsConfig } from '../aws-config';

class BedrockService {
  constructor() {
    this.client = null;
    this.toolSpecs = [];
    this.abortControllers = [];
  }

  async initializeClient() {
    if (this.client) {
      return this.client;
    }

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      const credentials = fromCognitoIdentityPool({
        client: new CognitoIdentityClient({ region: awsConfig.Agent.region }),
        identityPoolId: awsConfig.Agent.identityPoolId,
        logins: {
          [`cognito-idp.${awsConfig.Agent.region}.amazonaws.com/${awsConfig.Auth.Cognito.userPoolId}`]: idToken
        }
      });

      this.client = new BedrockRuntimeClient({
        region: awsConfig.Agent.region,
        credentials
      });

      return this.client;
    } catch (error) {
      console.error('Failed to initialize Bedrock client:', error);
      throw error;
    }
  }

  setToolSpecs(toolSpecs) {
    this.toolSpecs = toolSpecs;
  }

  async sendMessage(messages, modelId = 'global.anthropic.claude-haiku-4-5-20251001-v1:0', systemPrompt = null) {
    const client = await this.initializeClient();
    const abortController = new AbortController();
    this.abortControllers.push(abortController);

    try {
      const params = {
        modelId,
        messages,
        inferenceConfig: {
          maxTokens: 4096
        }
      };

      if (systemPrompt) {
        params.system = [{ text: systemPrompt }];
      }

      if (this.toolSpecs && this.toolSpecs.length > 0) {
        params.toolConfig = {
          tools: this.toolSpecs
        };
      }

      const command = new ConverseCommand(params);
      const response = await client.send(command, { abortSignal: abortController.signal });

      this.abortControllers = this.abortControllers.filter(c => c !== abortController);

      return {
        content: response.output.message.content,
        stopReason: response.stopReason,
        usage: response.usage
      };
    } catch (error) {
      this.abortControllers = this.abortControllers.filter(c => c !== abortController);
      console.error('Failed to send message:', error);
      throw error;
    }
  }

  async sendMessageStream(messages, modelId = 'global.anthropic.claude-haiku-4-5-20251001-v1:0', systemPrompt = null, onChunk = null) {
    const client = await this.initializeClient();
    const abortController = new AbortController();
    this.abortControllers.push(abortController);

    try {
      const params = {
        modelId,
        messages,
        inferenceConfig: {
          maxTokens: 4096
        }
      };

      if (systemPrompt) {
        params.system = [{ text: systemPrompt }];
      }

      if (this.toolSpecs && this.toolSpecs.length > 0) {
        params.toolConfig = {
          tools: this.toolSpecs
        };
      }

      const command = new ConverseStreamCommand(params);
      const response = await client.send(command, { abortSignal: abortController.signal });

      let fullContent = [];
      let usage = {};
      let stopReason = '';

      for await (const event of response.stream) {
        if (event.contentBlockStart) {
          if (event.contentBlockStart.start?.toolUse) {
            fullContent.push({
              toolUse: {
                toolUseId: event.contentBlockStart.start.toolUse.toolUseId,
                name: event.contentBlockStart.start.toolUse.name,
                input: ''
              }
            });
          }
        } else if (event.contentBlockDelta) {
          if (event.contentBlockDelta.delta?.text) {
            const text = event.contentBlockDelta.delta.text;
            if (fullContent.length === 0 || !fullContent[fullContent.length - 1].text) {
              fullContent.push({ text: text });
            } else {
              fullContent[fullContent.length - 1].text += text;
            }
            if (onChunk) {
              onChunk(text);
            }
          } else if (event.contentBlockDelta.delta?.toolUse) {
            const inputChunk = event.contentBlockDelta.delta.toolUse.input;
            if (fullContent.length > 0 && fullContent[fullContent.length - 1].toolUse) {
              fullContent[fullContent.length - 1].toolUse.input += inputChunk;
            }
          }
        } else if (event.messageStop) {
          stopReason = event.messageStop.stopReason;
        } else if (event.metadata) {
          usage = event.metadata.usage;
        }
      }

      for (const content of fullContent) {
        if (content.toolUse && typeof content.toolUse.input === 'string') {
          try {
            if (content.toolUse.input === '' || content.toolUse.input.trim() === '') {
              content.toolUse.input = {};
            } else {
              content.toolUse.input = JSON.parse(content.toolUse.input);
            }
          } catch (e) {
            console.error('Failed to parse tool input:', e, 'Input was:', content.toolUse.input);
            content.toolUse.input = {};
          }
        }
      }

      this.abortControllers = this.abortControllers.filter(c => c !== abortController);

      return {
        content: fullContent,
        stopReason,
        usage
      };
    } catch (error) {
      this.abortControllers = this.abortControllers.filter(c => c !== abortController);
      console.error('Failed to send streaming message:', error);
      throw error;
    }
  }

  abortAll() {
    this.abortControllers.forEach(controller => controller.abort());
    this.abortControllers = [];
  }
}

export default new BedrockService();
